import React, { useState } from 'react';
import { Check, Info, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { cn } from '@/utils/cn';

const variantStyles = {
  success: 'bg-green-50 border-green-200 text-green-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  error: 'bg-red-50 border-red-200 text-red-800',
} as const;

const variantIcons = {
  success: Check,
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
} as const;

interface AlertProps {
  variant?: keyof typeof variantStyles;
  /** Legacy alias for variant */
  type?: string;
  title?: React.ReactNode;
  /** Legacy alias for title */
  message?: React.ReactNode;
  children?: React.ReactNode;
  /** Legacy alias for children */
  description?: React.ReactNode;
  showIcon?: boolean;
  closable?: boolean;
  className?: string;
}

const Alert: React.FC<AlertProps> = ({
  variant,
  type,
  title,
  message,
  children,
  description,
  showIcon = true,
  closable,
  className,
}) => {
  const resolvedVariant = (variant ?? type ?? 'info') as keyof typeof variantStyles;
  const resolvedTitle = title ?? message;
  const resolvedChildren = children ?? description;
  const [closed, setClosed] = useState(false);
  if (closed) return null;

  const Icon = variantIcons[resolvedVariant];

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded border p-3 text-sm',
        variantStyles[resolvedVariant],
        className
      )}
    >
      {showIcon && <Icon className="h-4 w-4 mt-0.5 shrink-0" />}
      <div className="flex-1">
        {resolvedTitle && <div className="font-medium">{resolvedTitle}</div>}
        {resolvedChildren && (
          <div className={cn(resolvedTitle ? 'mt-1 text-xs opacity-80' : '')}>
            {resolvedChildren}
          </div>
        )}
      </div>
      {closable && (
        <button onClick={() => setClosed(true)} className="shrink-0 p-0.5 rounded hover:bg-black/5">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

export { Alert };
